import { createContext, useContext, useEffect, useMemo, useState } from "react";

import {
  getAllProjects,
  deleteProjectById,
  saveProject,
  getProjectCount,
  updateProjectTagById,
} from "../../api/projects.api";

import { UserContext } from "./user.context";

export const ProjectContext = createContext({
  userProjects: [],
  currentProject: null,
  loading: false,
  setUserProjects: () => null,
  setCurrentProject: () => null,
  loadProjects: async () => [],
  saveUserProject: async () => null,
  deleteSelectedProject: async () => null,
  updateSelectedProjectTag: async () => null,
  countUserProjects: async () => 0,
  clearCurrentProject: () => null,
  resetProjectState: () => null,
  selectProject: () => null,
});

export const ProjectProvider = ({ children }) => {
  const { user, currentUser } = useContext(UserContext);
  const authUser = user || currentUser;

  const [userProjects, setUserProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadProjects();
  }, [authUser?.uid]);

  const clearCurrentProject = () => {
    setCurrentProject(null);
    localStorage.removeItem("selectProjectID");
    localStorage.removeItem("selectProjectName");
  };

  const resetProjectState = () => {
    setCurrentProject(null);
    localStorage.removeItem("selectProjectID");
    localStorage.removeItem("selectProjectName");
  };

  const selectProject = (project) => {
    const selectedProject = project || null;

    setCurrentProject(selectedProject);

    if (selectedProject?.id) {
      localStorage.setItem("selectProjectID", selectedProject.id);
      localStorage.setItem("selectProjectName", selectedProject.name || "");
    } else {
      localStorage.removeItem("selectProjectID");
      localStorage.removeItem("selectProjectName");
    }
  };

  const loadProjects = async () => {
    try {
      if (!authUser?.uid) {
        setUserProjects([]);
        return [];
      }

      const response = await getAllProjects(authUser.uid);
      const projects = response?.projects || [];

      setUserProjects(projects);

      // 🔥 restore selected project from localStorage
      const storedProjectId = localStorage.getItem("selectProjectID");

      if (storedProjectId) {
        const matchedProject = projects.find((p) => p.id === storedProjectId);

        if (matchedProject) {
          setCurrentProject(matchedProject);
        }
      }

      return projects;
    } catch (error) {
      console.error(
        "loadProjects error:",
        error?.response?.data || error.message
      );
      resetProjectState();
      return [];
    } finally {
      setLoading(false);
    }
  };

  const saveUserProject = async (project) => {
    try {
      if (!authUser?.uid) {
        throw new Error("No authenticated user found.");
      }

      const response = await saveProject(authUser.uid, project);

      const savedProject = response?.project;

      if (!savedProject) {
        throw new Error("No project returned from save.");
      }

      setUserProjects((prevProjects) => {
        const existingIndex = prevProjects.findIndex(
          (currentProject) => currentProject.id === savedProject.id
        );

        if (existingIndex >= 0) {
          return prevProjects.map((currentProject) =>
            currentProject.id === savedProject.id
              ? savedProject
              : currentProject
          );
        }

        return [...prevProjects, savedProject];
      });

      return savedProject;
    } catch (error) {
      console.error(
        "saveUserProject error:",
        error?.response?.data || error.message
      );
      throw error;
    }
  };

  const deleteSelectedProject = async (projectId) => {
    try {
      if (!authUser?.uid) {
        throw new Error("No authenticated user found.");
      }

      await deleteProjectById(authUser.uid, projectId);

      setUserProjects((prev) => prev.filter((p) => p.id !== projectId));

      setCurrentProject((prev) => {
        if (prev?.id === projectId) {
          clearCurrentProject();
          return null;
        }
        return prev;
      });

      return true;
    } catch (error) {
      console.error(
        "deleteSelectedProject error:",
        error?.response?.data || error.message
      );
      throw error;
    }
  };

  const updateSelectedProjectTag = async (projectId, tag) => {
    try {
      if (!authUser?.uid) {
        throw new Error("No authenticated user found.");
      }

      const response = await updateProjectTagById(authUser.uid, projectId, tag);
      const updatedProject = response?.project;

      if (!updatedProject) {
        throw new Error("No updated project returned.");
      }

      setUserProjects((prevProjects) =>
        prevProjects.map((project) =>
          project.id === projectId ? updatedProject : project
        )
      );

      return updatedProject;
    } catch (error) {
      console.error(
        "updateSelectedProjectTag error:",
        error?.response?.data || error.message
      );
      throw error;
    }
  };

  const countUserProjects = async () => {
    try {
      if (!authUser?.uid) {
        return 0;
      }

      const response = await getProjectCount(authUser.uid);
      return response?.count || 0;
    } catch (error) {
      console.error(
        "countUserProjects error:",
        error?.response?.data || error.message
      );
      return 0;
    }
  };

  const value = useMemo(
    () => ({
      userProjects,
      currentProject,
      loading,
      setUserProjects,
      setCurrentProject,
      loadProjects,
      saveUserProject,
      deleteSelectedProject,
      updateSelectedProjectTag,
      countUserProjects,
      clearCurrentProject,
      resetProjectState,
      selectProject,
    }),
    [userProjects, currentProject, loading]
  );

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
};
